import { DAYS_SHORT, COLORS, esc, weekKey, dateISO, formatDuration } from './config.js';
import { state, getWeekBlocks } from './state.js';
import { loadTimeEntriesAPI } from './api.js';

// ══════════════════════════════════════════════════════════
// DASHBOARD — time-tracking stats for the visible week (modal overlay)
// Charts are plain CSS bars (no chart library), matching the vanilla stack.
// ══════════════════════════════════════════════════════════

// Duration of an entry in ms. Open entries (no ended_at) count up to now.
function entryMs(e) {
  const start = new Date(e.started_at).getTime();
  const end = e.ended_at ? new Date(e.ended_at).getTime() : Date.now();
  return Math.max(0, end - start);
}

// Day index (0=Mon … 6=Sun) of an entry within the visible week, or -1 if outside.
function dayIndexOf(e, mondayISO) {
  const d = new Date(e.started_at);
  const localISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // Compare against the 7 ISO dates of the week.
  const monday = new Date(mondayISO + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(day.getDate() + i);
    const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    if (iso === localISO) return i;
  }
  return -1;
}

function colorVar(colorId) {
  return COLORS.find(c => c.id === colorId) ? `var(--${colorId}-bd)` : 'var(--text-4)';
}

// ── Chart builders (return HTML strings) ────────────────
const HOUR_MS = 3600000;

// Per day: planned time (blocks of the visible week) next to tracked time, on a shared
// axis in whole hours so a few minutes no longer fill the chart.
function chartPerDay(entries, mondayISO) {
  const real = new Array(7).fill(0);
  entries.forEach(e => {
    const di = dayIndexOf(e, mondayISO);
    if (di >= 0) real[di] += entryMs(e);
  });
  const planned = new Array(7).fill(0);
  getWeekBlocks().forEach(b => { planned[b.day] += b.dur * 15 * 60000; });

  const axisH = Math.max(1, Math.ceil(Math.max(...real, ...planned) / HOUR_MS));
  const axisMs = axisH * HOUR_MS;
  const pct = ms => (ms / axisMs) * 100;
  const sum = arr => arr.reduce((a, b) => a + b, 0);

  const ticks = [axisH, axisH / 2, 0].map(h =>
    `<div class="dash-axis-tick" style="bottom:${pct(h * HOUR_MS)}%"><span>${h % 1 ? h.toFixed(1) : h}h</span></div>`
  ).join('');

  const bars = real.map((ms, i) => `
      <div class="dash-bar-col">
        <div class="dash-bar-track">
          <div class="dash-bar-fill is-planned${planned[i] ? '' : ' is-zero'}" style="height:${pct(planned[i])}%" title="Planificat: ${formatDuration(planned[i])}"></div>
          <div class="dash-bar-fill${ms ? '' : ' is-zero'}" style="height:${pct(ms)}%" title="Real: ${formatDuration(ms)}"></div>
        </div>
        <div class="dash-bar-label">${DAYS_SHORT[i]}</div>
      </div>`).join('');

  return `
    <section class="dash-section">
      <div class="dash-section-head">
        <h4>Hores per dia</h4>
        <span class="dash-section-total">${formatDuration(sum(real))} de ${formatDuration(sum(planned))} planificades</span>
      </div>
      <div class="dash-legend">
        <span><i class="dash-key"></i>Real</span>
        <span><i class="dash-key is-planned"></i>Planificat</span>
      </div>
      <div class="dash-chart">
        <div class="dash-axis">${ticks}</div>
        <div class="dash-bars">${bars}</div>
      </div>
    </section>`;
}

function chartPerType(entries) {
  const byType = new Map();  // typeId|'' → ms
  entries.forEach(e => {
    const key = e.block_type_id || '';
    byType.set(key, (byType.get(key) || 0) + entryMs(e));
  });
  const rows = [...byType.entries()].sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map(r => r[1]));

  if (rows.length === 0) {
    return `<section class="dash-section"><div class="dash-section-head"><h4>Temps per tipus</h4></div><p class="dash-empty">Sense dades.</p></section>`;
  }

  const html = rows.map(([typeId, ms]) => {
    const tp = state.blockTypes.find(t => t.id === typeId);
    const label = tp ? `${tp.emoji ? tp.emoji + ' ' : ''}${tp.label}` : 'Sense tipus';
    const color = tp ? colorVar(tp.color) : 'var(--text-4)';
    const pct = (ms / max) * 100;
    return `
      <div class="dash-hrow">
        <div class="dash-hrow-label">${esc(label)}</div>
        <div class="dash-hrow-track">
          <div class="dash-hrow-fill" style="width:${pct}%;background:${color}"></div>
        </div>
        <div class="dash-hrow-val">${formatDuration(ms)}</div>
      </div>`;
  }).join('');

  return `
    <section class="dash-section">
      <div class="dash-section-head"><h4>Temps per tipus</h4></div>
      <div class="dash-hrows">${html}</div>
    </section>`;
}

function chartEstVsReal(entries) {
  // Aggregate per task: real ms + estimate (minutes) + title.
  const byTask = new Map();
  entries.forEach(e => {
    const cur = byTask.get(e.task_id) || { ms: 0, est: e.estimate_min ?? null, title: e.task_title };
    cur.ms += entryMs(e);
    if (cur.est == null && e.estimate_min != null) cur.est = e.estimate_min;
    byTask.set(e.task_id, cur);
  });
  const rows = [...byTask.values()].sort((a, b) => b.ms - a.ms);

  if (rows.length === 0) {
    return `<section class="dash-section"><div class="dash-section-head"><h4>Estimat vs real</h4></div><p class="dash-empty">Cap tasca cronometrada aquesta setmana.</p></section>`;
  }

  const html = rows.map(r => {
    const realMin = r.ms / 60000;
    const estMin = r.est;
    const scale = Math.max(realMin, estMin || 0, 1);
    const realPct = (realMin / scale) * 100;
    const estPct = estMin != null ? (estMin / scale) * 100 : 0;
    let badge = '';
    if (estMin != null && estMin > 0) {
      const ratio = Math.round((realMin / estMin) * 100);
      const over = ratio > 110;
      badge = `<span class="dash-evr-badge${over ? ' is-over' : ''}">${ratio}%</span>`;
    }
    return `
      <div class="dash-evr-row">
        <div class="dash-evr-title">${esc(r.title || 'Tasca')}${badge}</div>
        <div class="dash-evr-bars">
          <div class="dash-evr-line">
            <span class="dash-evr-tag">real</span>
            <div class="dash-evr-track"><div class="dash-evr-fill is-real" style="width:${realPct}%"></div></div>
            <span class="dash-evr-num">${formatDuration(r.ms)}</span>
          </div>
          <div class="dash-evr-line">
            <span class="dash-evr-tag">est.</span>
            <div class="dash-evr-track"><div class="dash-evr-fill is-est" style="width:${estPct}%"></div></div>
            <span class="dash-evr-num">${estMin != null ? formatDuration(estMin * 60000) : '—'}</span>
          </div>
        </div>
      </div>`;
  }).join('');

  return `
    <section class="dash-section">
      <div class="dash-section-head"><h4>Estimat vs real</h4></div>
      <div class="dash-evr">${html}</div>
    </section>`;
}

// ── Open / close ────────────────────────────────────────
export async function openDashboard() {
  const overlay = document.getElementById('dashboard-overlay');
  const body = document.getElementById('dashboard-body');
  if (!overlay || !body) return;

  overlay.classList.add('open');
  body.innerHTML = `<p class="dash-empty">Carregant…</p>`;

  const mondayISO = weekKey(state.currentMonday);
  const sundayISO = dateISO(state.currentMonday, 6);
  const entries = await loadTimeEntriesAPI(mondayISO, sundayISO);

  body.innerHTML = entries.length === 0
    ? chartPerDay(entries, mondayISO) + `<p class="dash-empty">Encara no has cronometrat res aquesta setmana.<br>Prem <strong>▶</strong> al costat d'una tasca per començar.</p>`
    : chartPerDay(entries, mondayISO) + chartPerType(entries) + chartEstVsReal(entries);
}

export function closeDashboard() {
  document.getElementById('dashboard-overlay')?.classList.remove('open');
}
